/**
 * Offline-first SUAS Snapshot Service
 *
 * Handles queueing and syncing drone mission snapshots in RF-challenged environments.
 * Uses IndexedDB for local storage and syncs to the server when online.
 */

import { db, OfflineSnapshot } from './db';
import { mustDeferSend, sendTenantOf } from './client-id';
import { api } from '@/services/api';
import { networkService } from '@/services/network.service';

// Generate a UUID for offline records
function generateOfflineId(): string {
  return `offline_${crypto.randomUUID()}`;
}

export interface SnapshotLocation {
  lat: number;
  lng: number;
  altitude_meters?: number;
  heading?: number;
}

export interface SyncResult {
  success: boolean;
  synced: number;
  failed: number;
  errors: string[];
}

// Exponential backoff configuration
const RETRY_CONFIG = {
  maxRetries: 5,
  baseDelay: 1000, // 1 second
  maxDelay: 30000, // 30 seconds
};

class SuasOfflineService {
  private syncInProgress = false;
  private retryAttempts = new Map<string, number>();

  /**
   * Queue a snapshot for upload (works offline)
   */
  async queueSnapshot(
    missionId: string,
    blob: Blob,
    location: SnapshotLocation,
    capturedAt: string
  ): Promise<OfflineSnapshot> {
    const snapshot: OfflineSnapshot = {
      id: generateOfflineId(),
      mission_id: missionId,
      blob,
      lat: location.lat,
      lng: location.lng,
      altitude_meters: location.altitude_meters,
      heading: location.heading,
      captured_at: capturedAt,
      is_synced: false,
    };

    await db.offlineSnapshots.add(snapshot);

    // Try to sync immediately if online
    if (networkService.isOnline() && !this.syncInProgress) {
      this.syncSnapshots().catch(err => {
        console.warn('[SUAS] Background sync failed:', err);
      });
    }

    return snapshot;
  }

  /**
   * Get all snapshots for a mission (including pending)
   */
  async getSnapshotsForMission(missionId: string): Promise<OfflineSnapshot[]> {
    return db.offlineSnapshots
      .where('mission_id')
      .equals(missionId)
      .sortBy('captured_at');
  }

  /**
   * Get pending (unsynced) snapshot count
   */
  async getPendingCount(): Promise<number> {
    return db.offlineSnapshots
      .filter(s => !s.is_synced)
      .count();
  }

  /**
   * Get pending snapshots by mission
   */
  async getPendingByMission(): Promise<Map<string, number>> {
    const pending = await db.offlineSnapshots
      .filter(s => !s.is_synced)
      .toArray();

    const countByMission = new Map<string, number>();
    for (const snapshot of pending) {
      countByMission.set(
        snapshot.mission_id,
        (countByMission.get(snapshot.mission_id) || 0) + 1
      );
    }

    return countByMission;
  }

  /**
   * Sync a single snapshot to the server
   */
  private async syncSnapshot(snapshot: OfflineSnapshot): Promise<boolean> {
    // Contract 9 gate (client-id.ts mustDeferSend): never sent as someone else.
    if (mustDeferSend(snapshot as never)) return false;
    try {
      // Create FormData for upload
      const formData = new FormData();
      formData.append('photo', snapshot.blob, `snapshot_${snapshot.id}.jpg`);
      formData.append('lat', snapshot.lat.toString());
      formData.append('lng', snapshot.lng.toString());
      if (snapshot.altitude_meters !== undefined) {
        formData.append('altitude_meters', snapshot.altitude_meters.toString());
      }
      if (snapshot.heading !== undefined) {
        formData.append('heading', snapshot.heading.toString());
      }
      formData.append('captured_at', snapshot.captured_at);

      // Backend: POST /suas/missions/:missionId/snapshots (suas.routes.ts)
      // accepts exactly this payload — multipart "photo" file + app-provided
      // lat/lng/altitude_meters/heading/captured_at form fields. Unlike
      // /photos/upload it does NOT require EXIF GPS, so canvas-captured
      // snapshots sync correctly. On success the server stores the image in
      // S3 and inserts a flight_snapshots row; response is { data: snapshot }.
      // (Gap closed 2026-07 — this route previously did not exist and queued
      // snapshots 404'd forever.)
      // upload() (multipart), not post() — a FormData body on the JSON path is
      // rewritten to `{}` by axios before it is sent, so the snapshot image
      // never reaches the server and the queued snapshot fails on every retry.
      await api.upload(`/suas/missions/${snapshot.mission_id}/snapshots`, formData, sendTenantOf(snapshot));

      // Mark as synced
      await db.offlineSnapshots.update(snapshot.id, {
        is_synced: true,
        synced_at: new Date().toISOString(),
        sync_error: undefined,
      });

      // Clear retry count on success
      this.retryAttempts.delete(snapshot.id);

      return true;
    } catch (error) {
      console.error('[SUAS] Snapshot sync failed:', error);

      // Track retry attempts
      const attempts = (this.retryAttempts.get(snapshot.id) || 0) + 1;
      this.retryAttempts.set(snapshot.id, attempts);

      // Store error message
      await db.offlineSnapshots.update(snapshot.id, {
        sync_error: (error as Error).message,
      });

      return false;
    }
  }

  /**
   * Calculate exponential backoff delay
   */
  private getBackoffDelay(attempts: number): number {
    const delay = Math.min(
      RETRY_CONFIG.baseDelay * Math.pow(2, attempts),
      RETRY_CONFIG.maxDelay
    );
    // Add jitter to prevent thundering herd
    return delay + Math.random() * 1000;
  }

  /**
   * Sync all pending snapshots with retry logic
   */
  async syncSnapshots(): Promise<SyncResult> {
    const result: SyncResult = {
      success: true,
      synced: 0,
      failed: 0,
      errors: [],
    };

    if (!networkService.isOnline()) {
      result.success = false;
      result.errors.push('No network connection');
      return result;
    }

    if (this.syncInProgress) {
      result.success = false;
      result.errors.push('Sync already in progress');
      return result;
    }

    this.syncInProgress = true;

    try {
      const pending = await db.offlineSnapshots
        .filter(s => !s.is_synced)
        .sortBy('captured_at'); // Sync in chronological order

      for (const snapshot of pending) {
        // Never sent as someone else (S-06): another originator's row waits for them, or for the handoff.
        if (mustDeferSend(snapshot as never)) continue;
        const attempts = this.retryAttempts.get(snapshot.id) || 0;

        // Skip if max retries exceeded
        if (attempts >= RETRY_CONFIG.maxRetries) {
          result.failed++;
          result.errors.push(
            `Max retries exceeded for snapshot at ${snapshot.captured_at} (${attempts} attempts)`
          );
          continue;
        }

        // Apply exponential backoff if this is a retry
        if (attempts > 0) {
          const delay = this.getBackoffDelay(attempts - 1);
          console.info(`[SUAS] Retrying snapshot ${snapshot.id} after ${delay}ms (attempt ${attempts})`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }

        const success = await this.syncSnapshot(snapshot);
        if (success) {
          result.synced++;
        } else {
          result.failed++;
          result.errors.push(
            `Failed to sync snapshot at ${snapshot.captured_at} (attempt ${attempts + 1})`
          );
        }
      }

      result.success = result.failed === 0;
    } finally {
      this.syncInProgress = false;
    }

    return result;
  }

  /**
   * Clean up synced snapshots older than the specified number of days
   */
  async cleanupOldSnapshots(daysToKeep: number = 7): Promise<number> {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);
    const cutoffTimestamp = cutoffDate.toISOString();

    const toDelete = await db.offlineSnapshots
      .filter(s => s.is_synced && !!s.synced_at && s.synced_at < cutoffTimestamp)
      .toArray();

    const deleteIds = toDelete.map(s => s.id);
    await db.offlineSnapshots.bulkDelete(deleteIds);

    // Clean up retry tracking for deleted snapshots
    deleteIds.forEach(id => this.retryAttempts.delete(id));

    console.info(`[SUAS] Cleaned up ${deleteIds.length} synced snapshots older than ${daysToKeep} days`);
    return deleteIds.length;
  }

  /**
   * Clear all snapshots for a mission (use with caution)
   */
  async clearMissionSnapshots(missionId: string): Promise<number> {
    const snapshots = await db.offlineSnapshots
      .where('mission_id')
      .equals(missionId)
      .toArray();

    const deleteIds = snapshots.map(s => s.id);
    await db.offlineSnapshots.bulkDelete(deleteIds);

    // Clean up retry tracking
    deleteIds.forEach(id => this.retryAttempts.delete(id));

    return deleteIds.length;
  }

  /**
   * Clear only synced snapshots for a mission (keep pending)
   */
  async clearSyncedSnapshots(missionId: string): Promise<number> {
    const snapshots = await db.offlineSnapshots
      .where('mission_id')
      .equals(missionId)
      .filter(s => s.is_synced)
      .toArray();

    const deleteIds = snapshots.map(s => s.id);
    await db.offlineSnapshots.bulkDelete(deleteIds);

    return deleteIds.length;
  }

  /**
   * Get sync statistics
   */
  async getSyncStats(): Promise<{
    total: number;
    synced: number;
    pending: number;
    failed: number;
    oldestPending?: string;
  }> {
    const all = await db.offlineSnapshots.toArray();
    const pending = all.filter(s => !s.is_synced);
    const failed = pending.filter(s => s.sync_error);

    const stats = {
      total: all.length,
      synced: all.filter(s => s.is_synced).length,
      pending: pending.length,
      failed: failed.length,
      oldestPending: pending.length > 0
        ? pending.sort((a, b) => a.captured_at.localeCompare(b.captured_at))[0].captured_at
        : undefined,
    };

    return stats;
  }

  /**
   * Retry failed snapshots immediately (ignores backoff)
   */
  async retryFailed(): Promise<SyncResult> {
    const result: SyncResult = {
      success: true,
      synced: 0,
      failed: 0,
      errors: [],
    };

    if (!networkService.isOnline()) {
      result.success = false;
      result.errors.push('No network connection');
      return result;
    }

    const failed = await db.offlineSnapshots
      .filter(s => !s.is_synced && !!s.sync_error)
      .sortBy('captured_at');

    for (const snapshot of failed) {
      // Never sent as someone else (S-06): another originator's row waits for them, or for the handoff.
      if (mustDeferSend(snapshot as never)) continue;
      // Reset retry count for manual retry
      this.retryAttempts.delete(snapshot.id);

      const success = await this.syncSnapshot(snapshot);
      if (success) {
        result.synced++;
      } else {
        result.failed++;
        result.errors.push(`Failed to retry snapshot at ${snapshot.captured_at}`);
      }
    }

    result.success = result.failed === 0;
    return result;
  }

  // (registerBackgroundSync and the service worker's 'suas-snapshot-sync'
  // handler were removed in P2-05: nothing registered the tag, and the handler
  // only posted a message to a hook nothing mounted. Snapshots are synced by
  // the network listener below and by syncOrchestratorService.)
}

export const suasOfflineService = new SuasOfflineService();

// Auto-sync when network becomes available
if (typeof window !== 'undefined') {
  networkService.addListener((connected) => {
    if (connected) {
      console.info('[SUAS] Network available, triggering sync...');
      suasOfflineService.syncSnapshots().catch(err => {
        console.error('[SUAS] Auto-sync failed:', err);
      });
    }
  });
}
